import React from "react"
import {
  Button,
  Col,
  ConfigProvider,
  DatePicker,
  Descriptions,
  Icon,
  List,
  Modal,
  Row,
  Spin,
  Typography,
} from "antd"
import { navigate } from "gatsby"
import MediaContext from "../../utils/mediaContext"
import moment from "moment"
import style from "./SportingEvent.module.scss"

const MIN_STAYS = {
  Baseball: 1,
  Basketball: 1,
  Football: 2,
  Soccer: 1,
  Softball: 1,
  "Track & Field": 1,
  Volleyball: 1,
}

const makeURL = (start, end) =>
  `${process.env.BOOKING_ENGINE}` +
  `?nextcommand=roomsearch` +
  `&date_from=${start}` +
  `&date_to=${end}`

export const CalendarItem = ({ sportingEvent, viewDetails }) => {
  if (!sportingEvent) return null

  const {
    awayTeamLogo,
    awayTeamName,
    category,
    eventName,
    eventStart,
    eventType,
    homeTeamLogo,
    homeTeamName,
    reservationEnd,
    reservationStart,
  } = sportingEvent

  return (
    <div className={style.itemWrapper}>
      <div className={style.logos}>
        <div className={style.awayLogo}>
          <img alt={awayTeamLogo.description} src={awayTeamLogo.file.url} />
          <Typography.Text>{awayTeamName}</Typography.Text>
        </div>
        <div className={style.vs}>
          <Typography.Text strong>vs</Typography.Text>
        </div>
        <div className={style.homeLogo}>
          <img alt={homeTeamLogo.description} src={homeTeamLogo.file.url} />
          <Typography.Text>{homeTeamName}</Typography.Text>
        </div>
      </div>
      <div className={style.name}>
        <Typography.Text strong>{eventName}</Typography.Text>
      </div>
      <div className={style.sport}>
        <div>
          <Typography.Text style={{ color: `#fff` }}>
            {category ? category : null} {eventType}
          </Typography.Text>
        </div>
      </div>
      <div className={style.date}>
        <Typography.Text>
          {moment(eventStart).format(`dddd, MMMM Do [at] h:mm a`)}
        </Typography.Text>
      </div>
      <div className={style.actions}>
        <div className={style.btn}>
          <Button block onClick={viewDetails}>
            Details
          </Button>
        </div>
        <div className={style.btn}>
          <a href={makeURL(reservationStart, reservationEnd)}>
            <Button block type="primary">
              Check Availability
            </Button>
          </a>
        </div>
      </div>
    </div>
  )
}

export const EventDetails = ({ onCancel, sportingEvent, visible }) => {
  const [dates, setDates] = React.useState({})
  const [eventDate, setEventDate] = React.useState(null)
  const { width } = React.useContext(MediaContext)

  React.useEffect(() => {
    if (visible && sportingEvent) {
      const eventStart = moment(sportingEvent.eventStart)
      const reservationStart =
        sportingEvent.reservationStart ||
        moment(eventStart).subtract(1, `d`).format(`YYYY-MM-DD`)
      const reservationEnd =
        sportingEvent.reservationEnd ||
        moment(eventStart).add(1, `d`).format(`YYYY-MM-DD`)
      setDates({ reservationStart, reservationEnd })
      setEventDate(moment(eventStart).format(`YYYY-MM-DD`))
    }
  }, [sportingEvent, visible])

  React.useEffect(() => {
    const { reservationStart, reservationEnd } = dates
    if (sportingEvent && moment(reservationEnd).isBefore(reservationStart)) {
      const offset = MIN_STAYS[sportingEvent.eventType]
      const checkout = moment(reservationStart).add(offset, `d`)
      setDates({
        reservationStart,
        reservationEnd: checkout.format(`YYYY-MM-DD`),
      })
    }
  }, [dates, sportingEvent])

  if (!sportingEvent) return null

  const {
    awayTeamLogo,
    category,
    eventName,
    eventStart,
    eventType,
    homeTeamLogo,
    ticketPrice,
    venue,
  } = sportingEvent

  const onDateChange = (key, val) => {
    setDates({ ...dates, [key]: val.format("YYYY-MM-DD") })
  }

  const dateRenderer = cur => {
    const isCheckIn = cur.isSame(dates.reservationStart)
    const isCheckOut = cur.isSame(dates.reservationEnd)
    const isDayOfEvent = cur.isSame(eventDate)
    const isBetween = cur.isBetween(
      dates.reservationStart,
      dates.reservationEnd
    )

    let style = {}
    if (isCheckIn || isCheckOut) {
      style = {
        ...style,
        background: `rgb(66, 150, 180)`,
        borderRadius: 0,
        color: `#fff`,
        width: `auto`,
      }
    }
    if (isCheckIn) {
      style = {
        ...style,
        borderBottomLeftRadius: 4,
        borderTopLeftRadius: 4,
      }
    }
    if (isCheckOut) {
      style = {
        ...style,
        borderBottomRightRadius: 4,
        borderTopRightRadius: 4,
      }
    }
    if (isBetween) {
      style = {
        ...style,
        background: `rgb(66 150 180 / 30%)`,
        borderRadius: 0,
        width: `auto`,
      }
    }
    if (isDayOfEvent) {
      style = {
        ...style,
        background: `#ff9600`,
        color: `#fff`,
        fontWeight: `bold`,
        width: `auto`,
      }
    }

    return (
      <div className="ant-calendar-date" style={style}>
        {cur.date()}
      </div>
    )
  }

  const pickerProps = {
    allowClear: false,
    className: style.datePicker,
    dateRender: dateRenderer,
    format: width < 400 ? `ddd, MMM Do` : `dddd, MMMM Do`,
    suffixIcon: <Icon type="edit" />,
    showToday: false,
  }

  const calcEarliestCheckout = () => {
    const offset = MIN_STAYS[eventType]
    return moment(dates.reservationStart).add(offset, `d`)
  }

  const layout = width < 500 ? `horizontal` : `vertical`

  return (
    <Modal
      afterClose={() => setDates({})}
      centered
      footer={null}
      onCancel={onCancel}
      style={{ maxWidth: `90vw` }}
      visible={visible}
    >
      <Row
        align="middle"
        justify="space-around"
        style={{ margin: `12px auto 24px`, textAlign: `center` }}
        type="flex"
      >
        <Col span={10}>
          <img
            alt={awayTeamLogo.description}
            src={awayTeamLogo.file.url}
            style={{ height: 60, maxWidth: `80%` }}
          />
        </Col>
        <Col span={4}>
          <Typography.Text strong>VS</Typography.Text>
        </Col>
        <Col span={10}>
          <img
            alt={homeTeamLogo.description}
            src={homeTeamLogo.file.url}
            style={{ height: 60, maxWidth: `80%` }}
          />
        </Col>
      </Row>
      <Descriptions bordered layout={layout} size="small">
        <Descriptions.Item label="Who" span={3}>
          {eventName}
        </Descriptions.Item>
        <Descriptions.Item label="What" span={3}>
          {category ? `${category} ` : ``}
          {eventType}
        </Descriptions.Item>
        <Descriptions.Item label="When" span={3}>
          {moment(eventStart).format(
            layout === `horizontal`
              ? `ddd, MMM Do @ h:mm a`
              : `dddd, MMMM Do @ h:mm a`
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Where" span={3}>
          {venue.name}
          {venue.phoneNumber ? (
            <>
              <br />
              <a href={`tel:${venue.phoneNumber}`}>{venue.phoneNumber}</a>
            </>
          ) : null}
          {venue.address ? (
            <>
              <br />
              {venue.address}
              <br />
              <a
                href={`https://google.com/maps/place/${encodeURI(
                  venue.address
                )}/`}
              >
                View Map
              </a>
            </>
          ) : null}
          {venue.website ? (
            <>
              {` / `}
              <a href={venue.website}>Visit Website</a>
            </>
          ) : null}
        </Descriptions.Item>
        {ticketPrice ? (
          <Descriptions.Item label="Price" span={3}>
            {ticketPrice}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Arrive" span={3}>
          <DatePicker
            {...pickerProps}
            disabledDate={cur => cur && cur < moment().startOf(`day`)}
            onChange={val => onDateChange(`reservationStart`, val)}
            value={moment(dates.reservationStart)}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Depart" span={3}>
          <DatePicker
            {...pickerProps}
            disabledDate={cur => {
              const checkout = calcEarliestCheckout()
              return cur && cur < checkout.startOf(`day`)
            }}
            onChange={val => onDateChange(`reservationEnd`, val)}
            value={moment(dates.reservationEnd)}
          />
        </Descriptions.Item>
      </Descriptions>
      <Row align="middle" type="flex" style={{ margin: `24px auto` }}>
        <Col span={10} style={{ paddingRight: 6 }}>
          <Button block onClick={onCancel} size="large">
            Close
          </Button>
        </Col>
        <Col span={14} style={{ paddingLeft: 6 }}>
          <a href={makeURL(dates.reservationStart, dates.reservationEnd)}>
            <Button block size="large" type="primary">
              Book a Room
            </Button>
          </a>
        </Col>
      </Row>
    </Modal>
  )
}

const EmptyList = () => (
  <div className={style.emptyList}>
    <Icon
      style={{ fontSize: 48 }}
      theme="twoTone"
      twoToneColor="#4296b4"
      type="calendar"
    />
    <span style={{ fontSize: 22, marginTop: 16 }}>No Events Scheduled</span>
    <span>Please check back another time.</span>
    <Button
      icon="arrow-left"
      onClick={() => navigate(`/sports/`)}
      size="large"
      style={{ margin: `24px auto` }}
      type="primary"
    >
      Back
    </Button>
  </div>
)

const LoadingEvents = () => (
  <Spin
    indicator={
      <Icon
        type="loading"
        spin
        style={{ fontSize: `32px`, marginBottom: `16px` }}
      />
    }
    style={{
      alignItems: `center`,
      background: `#f9f9f9`,
      display: `flex`,
      flexDirection: `column`,
      height: `300px`,
      justifyContent: `center`,
      width: `100%`,
    }}
    tip="Checking for upcoming events..."
  />
)

export const EventsList = ({ events, noEvents }) => {
  const [modalVis, setModalVis] = React.useState(false)
  const [currentEvent, setCurrentEvent] = React.useState(null)

  const toggleModal = () => setModalVis(!modalVis)
  const viewDetails = evt => {
    setCurrentEvent(evt)
    setModalVis(true)
  }

  return (
    <ConfigProvider renderEmpty={noEvents ? EmptyList : LoadingEvents}>
      {!!events.length && (
        <EventDetails
          onCancel={toggleModal}
          sportingEvent={currentEvent}
          visible={modalVis}
        />
      )}
      <List
        dataSource={events}
        itemLayout="horizontal"
        renderItem={(evt, i) => (
          <CalendarItem
            key={i}
            sportingEvent={evt}
            viewDetails={() => viewDetails(evt)}
          />
        )}
      />
    </ConfigProvider>
  )
}
